import type { Department } from "../../contexts/AuthProvider";
import { DEFAULT_TARGET_PPD } from "../../utils/consts";
import { computeHppdBreakdown } from "../../utils/hppdBreakdown";
import {
	type DayStats,
	type HospitalData,
	HppdSeparationType,
	hppdTargetForDate,
	type LocationMode,
	type NurseFullRole,
	type NurseInShift,
} from "../../utils/models";
import { computeDirectAndNursingHppd, filterNursesForStats } from "../../utils/statsCalculations";
import { isShiftInTotalHours, totalHoursOfShift } from "../../utils/utilFunctions";
import { resolveRoleShortCode } from "../dailyPrintout/util/roleHelpers";
import { censusGroupsOf, type FindDepartmentLocation } from "./aiPrintoutCensusGroups";
import { type AiPrintoutOrders, rankIn, roleFields } from "./aiPrintoutRowMappers";
import type { AiPrintoutMetrics, AiPrintoutShift } from "./contract";

/** Everything the Day view's HPPD and the printouts' DHPPD/NHPPD are computed from, besides the shifts. */
export type AiPrintoutStatsInputs = {
	department: Department;
	hospitalData: HospitalData;
	weekIndex: number;
	dayIndex: number;
	jobManagementEnabled: boolean;
	/** `!isHospital`: a hospital department counts only `rolesToDisplay` toward hours. */
	isSnf: boolean;
	rolesToDisplay: NurseFullRole[];
	/** The viewer's `printout_hppd_include_trainees` setting, which only DHPPD and NHPPD follow. */
	hppdIncludeTrainees: boolean;
	/** The viewer's census grouping (`censusByLocation`), which decides the Day view's location groups. */
	censusGrouping: LocationMode;
	findDepartmentLocation: FindDepartmentLocation;
};

export type AiPrintoutShiftHppdFields = Pick<AiPrintoutShift, "countsTowardHppd" | "countsTowardDhppd" | "countsTowardNhppd" | "hppdHours" | "countsTowardPpd">;

// DEFAULT: as `calculateDayStats` and the Summary printout — a department with no break rules deducts no breaks.
const breakRulesOf = (department: Department) => department.breakRulesConfig ?? [];

/**
 * Per shift, the answers the day's totals were computed with: `calculateDayStats`'s HPPD (no trainees, no
 * PPD-excluded staff), `computeDirectAndNursingHppd`'s DHPPD/NHPPD, and the shared PPD filter — each through the
 * same per-shift predicate and hours as `getTotalHours`, so partial sums add up to the totals.
 */
export const createShiftHppdFields = (
	inputs: AiPrintoutStatsInputs,
	unionByNurseId: Map<number, string>,
): ((shift: NurseInShift, isActual: boolean) => AiPrintoutShiftHppdFields) => {
	const { department, hospitalData, jobManagementEnabled, isSnf, rolesToDisplay, hppdIncludeTrainees } = inputs;
	return (shift, isActual) => {
		const counts = (includeTrainees: boolean, includePpdExcluded: boolean): boolean =>
			isShiftInTotalHours(shift, {
				showActual: isActual,
				isSnf,
				rolesToDisplay,
				jobManagementEnabled,
				allNurses: hospitalData.nurses,
				includeTrainees,
				includePpdExcluded,
			});
		return {
			countsTowardHppd: counts(false, false),
			countsTowardDhppd: counts(hppdIncludeTrainees, true),
			countsTowardNhppd: counts(hppdIncludeTrainees, false),
			hppdHours: totalHoursOfShift(department.department_id, shift, breakRulesOf(department), jobManagementEnabled, unionByNurseId),
			countsTowardPpd: filterNursesForStats(shift, jobManagementEnabled, hospitalData.nurses, false, false),
		};
	};
};

export interface SideMetricsArgs {
	inputs: AiPrintoutStatsInputs;
	isActual: boolean;
	/** This side's shifts: what `getTotalHours` reads for the day on this side. */
	shifts: NurseInShift[];
	/** The Day view's stats for this side (`weekViewData`'s `dayStats` / `dayStatsActual`). */
	dayStats: DayStats;
	census: number | null;
	/** The viewer sees wages here, the department isn't a hospital, and the day has wage figures for this side. */
	wagesVisible: boolean;
	/** Luxon weekday of the schedule day (Monday 1 … Sunday 7), which the HPPD target is keyed by. */
	weekday: number;
	orders: AiPrintoutOrders;
	/** The roster's unions (`buildNurseUnionMap`), built once per printout. */
	unionByNurseId: Map<number, string>;
}

/** HPPD by dimension over a census, `computeHppdBreakdown` — the Day view's calculator. */
const breakdownOf = (args: SideMetricsArgs, shifts: NurseInShift[], census: number, separation: HppdSeparationType) => {
	const { department, hospitalData, jobManagementEnabled } = args.inputs;
	return computeHppdBreakdown(
		shifts,
		census,
		department.department_id,
		breakRulesOf(department),
		separation,
		department,
		jobManagementEnabled,
		hospitalData.nurses,
		args.unionByNurseId,
	);
};

/** The Day view's HPPD by role, its short-code keys read back to each role. */
const hppdByRole = (args: SideMetricsArgs, census: number): AiPrintoutMetrics["byRole"] => {
	const roleByShort = new Map(args.shifts.map((shift) => [resolveRoleShortCode(shift.role, undefined), shift.role]));
	return breakdownOf(args, args.shifts, census, HppdSeparationType.Role).map((entry) => {
		const role = roleByShort.get(entry.key);
		if (role === undefined) throw new Error(`HPPD breakdown key "${entry.key}" matches no role on the day`);
		return { ...roleFields(role), roleRank: rankIn(args.orders.roleRanks, role), hppd: entry.hppd };
	});
};

/** Each Day view census group's counted hours over the group's census, by the same calculator with no split. */
const hppdByLocation = (args: SideMetricsArgs, census: number): Pick<AiPrintoutMetrics, "byLocation" | "byLocationGrouping"> => {
	const { inputs } = args;
	const grouped = censusGroupsOf({
		department: inputs.department,
		hospitalData: inputs.hospitalData,
		weekIndex: inputs.weekIndex,
		dayIndex: inputs.dayIndex,
		mode: inputs.censusGrouping,
		totalCensus: census,
		shifts: args.shifts,
		findDepartmentLocation: inputs.findDepartmentLocation,
	});
	if (grouped === null) return { byLocation: null, byLocationGrouping: null };
	return {
		byLocationGrouping: grouped.grouping,
		byLocation: grouped.groups.map((group) => {
			const [total] = breakdownOf(args, group.shifts, group.census, HppdSeparationType.None);
			// The breakdown leaves out a group with no counted hours or no census: an HPPD of 0, as the Day view shows it.
			return { ...group.fields, census: group.census, hppd: total === undefined ? 0 : total.hppd };
		}),
	};
};

const directAndNursingHppd = (args: SideMetricsArgs, census: number) => {
	const { department, hospitalData, weekIndex, dayIndex, jobManagementEnabled, isSnf, rolesToDisplay, hppdIncludeTrainees } = args.inputs;
	return computeDirectAndNursingHppd({
		weeklyShifts: hospitalData.weeks[weekIndex].weeklyShifts,
		dayIndex,
		showActual: args.isActual,
		isSnf,
		rolesToDisplay,
		departmentId: department.department_id,
		breakRulesConfig: breakRulesOf(department),
		jobManagementEnabled,
		allNurses: hospitalData.nurses,
		includeTrainees: hppdIncludeTrainees,
		census,
	});
};

/** One side's metrics: the Day view's own stats, the Summary printout's DHPPD/NHPPD, and the Day view's breakdowns. */
export const buildSideMetrics = (args: SideMetricsArgs): AiPrintoutMetrics => {
	const { inputs, dayStats, census, wagesVisible } = args;
	const hppdTarget = hppdTargetForDate(inputs.department.hppd_target, args.weekday);
	const variants = census === null ? null : directAndNursingHppd(args, census);
	return {
		nursingHours: dayStats.totalHours,
		hppd: census === null ? null : dayStats.hppd,
		dhppd: variants === null ? null : variants.dhppd,
		nhppd: variants === null ? null : variants.nhppd,
		// A target of 0 is a department that set none: the Day view's chip has nothing to compare against either.
		hppdTarget: hppdTarget > 0 ? hppdTarget : null,
		ppd: wagesVisible && census !== null ? dayStats.ppd : null,
		// The Day view shows a PPD target only above the configuration's placeholder value.
		ppdTarget: wagesVisible && inputs.department.ppd > DEFAULT_TARGET_PPD ? inputs.department.ppd : null,
		laborCost: wagesVisible ? dayStats.totalSpent : null,
		byRole: census === null ? [] : hppdByRole(args, census),
		...(census === null ? { byLocation: null, byLocationGrouping: null } : hppdByLocation(args, census)),
	};
};
